import I18n from 'i18n-js';
import {
  DeviceManager,
  LocalStorage,
  FeatureDetector,
  Logger,
} from '@eyeson/js';
import React, { useEffect, useRef, useState } from 'react';
import { withStyles, makeStyles, useTheme } from '@material-ui/styles';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContentText from '@material-ui/core/DialogContentText';
import {
  Button,
  Select,
  MenuItem,
  Typography,
  Box,
  Slider,
  Switch,
  Tooltip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Icon,
  RadioGroup,
  Radio,
} from '@material-ui/core';
import HelpIcon from '@material-ui/icons/HelpOutline';
import { red } from '@material-ui/core/colors';
import Video from '../streams/Video';
import eyesonSecondCamHelper from '../../utils/eyesonSecondCamHelper.js';
import classNames from 'classnames';
import QRCode from 'qrcode.react';
import supports from '../../utils/SupportHelper.js';
import { ReactComponent as ImageCover } from '../../assets/yard_cover.svg';
import { ReactComponent as ImageContain } from '../../assets/yard_contain.svg';

const canShare = FeatureDetector.canShare();
const _scope = 'second-cam.';

const StyledButton = withStyles((theme) => ({
  root: {
    margin: theme.spacing(1),
  },
  label: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
  },
}))(Button);

const StopButton = withStyles((theme) => ({
  root: {
    margin: theme.spacing(1),
    color: red[500],
    borderColor: red[500],
    '&:hover': {
      color: red[700],
      borderColor: red[700],
    },
  },
}))(Button);

const StyledSlider = withStyles((theme) => ({
  root: {
    color:
      theme.palette.type === 'light'
        ? theme.palette.primary.dark
        : theme.palette.common.white,
  },
  track: {
    backgroundColor: theme.palette.secondary[theme.palette.type],
  },
  thumb: {
    backgroundColor: theme.palette.secondary[theme.palette.type],
    '&:hover': {
      boxShadow:
        theme.palette.type === 'light'
          ? '0px 0px 0px 8px rgba(0,0,0,.14)'
          : '0px 0px 0px 8px rgba(255,255,255,.16)',
    },
    '&.Mui-focusVisible': {
      boxShadow:
        theme.palette.type === 'light'
          ? '0px 0px 0px 14px rgba(0,0,0,.14)'
          : '0px 0px 0px 14px rgba(255,255,255,.16)',
    },
  },
  active: {
    '&:hover': {
      boxShadow:
        theme.palette.type === 'light'
          ? '0px 0px 0px 14px rgba(0,0,0,.14)'
          : '0px 0px 0px 14px rgba(255,255,255,.16)',
    },
  },
}))(Slider);

const useStyles = makeStyles((theme) => ({
  split: {
    display: 'grid',
    gap: '1rem',
    gridTemplateColumns: '1fr 1fr',
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: '1em',
  },
  input: {
    display: 'flex',
  },
  actions: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
  },
  pushLeft: {
    marginInlineEnd: 'auto',
  },
  sliderRow: {
    display: 'grid',
    gridTemplateColumns: 'max-content 50% auto',
    alignItems: 'center',
    gap: '1rem',
    marginBlockStart: theme.spacing(3),
  },
  qrSection: {
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    alignItems: 'start',
    gap: '1rem',
    marginBlockStart: theme.spacing(3),
  },
  alignRight: {
    display: 'flex',
  },
  radioGroup: {
    display: 'block',
  },
  fitIcon: {
    width: '2.5rem',
    verticalAlign: 'middle',
    marginInlineEnd: '0.5rem',
  },
}));

const findActiveDevice = (stream, type = 'Video') => {
  if (!stream) {
    return null;
  }
  const tracks = stream[`get${type}Tracks`]();
  if (tracks.length === 0) {
    return null;
  }
  const { deviceId } = tracks[0].getSettings();
  return deviceId;
};

const SecondCameraDialog = ({
  onEvent,
  onClose,
  token,
  options,
  recvOnlyMode,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.type === 'dark';
  const _startHandler = useRef(null);
  const _deviceManager = useRef(null);
  const [videoStream, setVideoStream] = useState(null);
  const [cameraList, setCameraList] = useState([
    {
      deviceId: 'external-camera',
      label: I18n.t('label:select:external-camera'),
    },
  ]);
  const [activeCamera, setActiveCamera] = useState('');
  const [microphoneList, setMicrophoneList] = useState([
    { deviceId: 'none', label: I18n.t('label:select:no_microphone') },
  ]);
  const [activeMicrophone, setActiveMicrophone] = useState('none');
  const [audioPassthrough, setAudioPassthrough] = useState(false);
  const [currentState, setCurrentState] = useState(
    eyesonSecondCamHelper.getCurrentState()
  );
  const [zoomValue, setZoomValue] = useState(
    DeviceManager.getStoredZoom(_scope) || 1
  );
  const [copied, setCopied] = useState(false);
  const [fitContent, setFitContent] = useState('zoom');

  const externalCameraUrl = `${document.baseURI}external-camera?${token}`;
  // const externalCameraUrl = `https://551b536822f4.ngrok-free.app/external-camera?${token}`;

  const classes = useStyles();

  const startCamera = () => {
    if (!_deviceManager.current) {
      return;
    }
    if (currentState === 'active') {
      eyesonSecondCamHelper.stop();
      onClose();
      return;
    }
    _deviceManager.current.storeConstraints();
    const cb = (event) => {
      if (eyesonSecondCamHelper.getCurrentState() === 'active') {
        onClose();
      }
      if (event.type === 'warning') {
        eyesonSecondCamHelper.offEvent(cb);
        _startHandler.current = null;
        onEvent(event);
      }
    };
    eyesonSecondCamHelper.onEvent(cb);
    _startHandler.current = cb;
    const options = {
      scope: _scope,
      sendOnly: true,
      video: true,
      audio: activeMicrophone !== 'none',
      audioPassthrough,
      zoom: zoomValue > 1,
    };
    LocalStorage.store(_scope + 'options', {
      scope: _scope,
      video: true,
      audio: activeMicrophone !== 'none',
      audioPassthrough,
      fitContent,
    });
    eyesonSecondCamHelper.setOptions({ fitContent });
    eyesonSecondCamHelper.start(options);
  };

  const applyCamera = () => {
    if (!_deviceManager.current) {
      return;
    }
    _deviceManager.current.storeConstraints();
    const options = {
      video: true,
      audio: activeMicrophone !== 'none',
      audioPassthrough,
      zoom: zoomValue > 1,
    };
    LocalStorage.store(_scope + 'options', {
      scope: _scope,
      video: true,
      audio: activeMicrophone !== 'none',
      audioPassthrough,
      fitContent,
    });
    eyesonSecondCamHelper.apply(options, fitContent);
    onClose();
  };

  const changeCamera = ({ target }) => {
    const cameraId = target.value;
    setVideoStream(null);
    if (cameraId === 'external-camera') {
      setActiveCamera(cameraId);
    } else {
      _deviceManager.current.setVideoInput(cameraId);
    }
  };

  const changeMicrophone = ({ target }) => {
    const microphoneId = target.value;
    setActiveMicrophone(microphoneId);
    if (microphoneId !== 'none') {
      _deviceManager.current.setAudioInput(microphoneId, {
        preventUpdate: true,
      });
    }
  };

  const handleAudioPassthroughChange = () => {
    const newAudioPassthrough = !audioPassthrough;
    setAudioPassthrough(newAudioPassthrough);
    _deviceManager.current.setAudioPassthrough(newAudioPassthrough, {
      preventUpdate: true,
    });
  };

  const updateZoom = (_event, value) => {
    setZoomValue(value);
    if (value !== zoomValue && _deviceManager.current) {
      _deviceManager.current.setZoom(value);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(externalCameraUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      Logger.error('ExternalCamera::handleCopy', error);
      onEvent({ type: 'warning', name: 'label:copy_error' });
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({ url: externalCameraUrl });
    } catch (error) {
      if (error.name !== 'AbortError') {
        Logger.error('ExternalCamera::handleShare', error);
        onEvent({ type: 'warning', name: 'label:copy_error' });
      }
    }
  };

  useEffect(() => {
    if (_deviceManager.current) {
      return;
    }
    if (recvOnlyMode) {
      setActiveCamera('external-camera');
      return;
    }
    setCurrentState(eyesonSecondCamHelper.getCurrentState());
    let initStream = true;
    const options = LocalStorage.load(_scope + 'options', {
      audio: false,
      video: true,
      scope: _scope,
      audioPassthrough: false,
      fitContent: 'zoom',
    });
    setAudioPassthrough(options.audioPassthrough);
    if (options.fitContent) {
      setFitContent(options.fitContent);
    }
    let deviceManager = new DeviceManager(options);
    const zoom = DeviceManager.getStoredZoom(_scope) || 1;
    deviceManager.setZoom(zoom);
    _deviceManager.current = deviceManager;
    deviceManager.onChange((event) => {
      const { stream, cameras, microphones } = event;
      if (stream) {
        const cameraId = findActiveDevice(stream, 'Video');
        const microphoneId = findActiveDevice(stream, 'Audio');
        setVideoStream(stream);
        if (cameraId) {
          setActiveCamera(cameraId);
        }
        if (microphoneId) {
          if (initStream && options.audio) {
            setActiveMicrophone(microphoneId);
          }
        }
        initStream = false;
      }
      if (cameras) {
        setCameraList(
          [
            {
              deviceId: 'external-camera',
              label: I18n.t('label:select:external-camera'),
            },
          ].concat(cameras)
        );
      }
      if (microphones) {
        setMicrophoneList(
          [
            { deviceId: 'none', label: I18n.t('label:select:no_microphone') },
          ].concat(microphones)
        );
      }
    });
    deviceManager.start();
    const onCamUpdate = () => {
      const state = eyesonSecondCamHelper.getCurrentState();
      setCurrentState(state);
    };
    eyesonSecondCamHelper.onEvent(onCamUpdate);
    return () => {
      if (_deviceManager.current) {
        eyesonSecondCamHelper.offEvent(onCamUpdate);
        if (_startHandler.current) {
          eyesonSecondCamHelper.offEvent(_startHandler.current);
          _startHandler.current = null;
        }
        _deviceManager.current.terminate();
        _deviceManager.current = null;
      }
    };
  }, [recvOnlyMode]);

  return (
    <>
      <DialogContentText>
        {I18n.t('dialog:second_camera:description')}
      </DialogContentText>
      <Box marginTop={2}>
        {activeCamera === '' ? (
          <Typography className="video-wrap-loading">
            {I18n.t('label:loading')}
          </Typography>
        ) : (
          <div className={classes.split}>
            <div
              className={classNames('video-wrap', { 'no-cam': !videoStream })}
            >
              <Video stream={videoStream} muted={true} pipEnabled={false} />
            </div>
            <div>
              <Typography variant="overline" className={classes.label}>
                {I18n.t('devices:camera')}
              </Typography>
              <Select
                className={classes.input}
                value={activeCamera}
                onChange={changeCamera}
                color="secondary"
                SelectDisplayProps={{
                  'aria-label': I18n.t('devices:camera'),
                }}
              >
                {cameraList.map(({ deviceId, label }) => {
                  return (
                    <MenuItem key={deviceId} value={deviceId}>
                      {label}
                    </MenuItem>
                  );
                })}
              </Select>
              {activeCamera === 'external-camera' ? (
                <div className={classes.qrSection}>
                  <QRCode
                    value={externalCameraUrl}
                    bgColor={isDarkMode ? '#fafafa' : '#f2f2f2'}
                    fgColor={isDarkMode ? '#353535' : '#212121'}
                    includeMargin={true}
                    aria-label={I18n.t('aria:label:qrcode_link', {
                      defaultValue: 'QR code link',
                    })}
                  />
                  <div>
                    <Typography variant="body2">
                      {I18n.t(
                        'dialog:presentation:second_camera:external-camera'
                      )}
                    </Typography>
                    <p>
                      {supports.ClipboardAPI && (
                        <Tooltip
                          title={
                            copied
                              ? I18n.t('label:copied')
                              : I18n.t('label:copy_link')
                          }
                        >
                          <IconButton
                            edge="start"
                            onClick={handleCopy}
                            aria-label={I18n.t('label:copy_link', {
                              defaultValue: 'Copy link',
                            })}
                          >
                            <Icon>file_copy</Icon>
                          </IconButton>
                        </Tooltip>
                      )}
                      {canShare && (
                        <Tooltip title={I18n.t('label:share_link')}>
                          <IconButton
                            onClick={handleShare}
                            aria-label={I18n.t('aria:label:share_link', {
                              defaultValue: 'Share link',
                            })}
                          >
                            <Icon>share</Icon>
                          </IconButton>
                        </Tooltip>
                      )}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className={classes.sliderRow}>
                    <Typography variant="overline">
                      {I18n.t('label:zoom')}
                    </Typography>
                    <StyledSlider
                      value={zoomValue}
                      min={1}
                      max={4}
                      step={0.1}
                      onChange={updateZoom}
                    />
                    <span>{zoomValue}&times;</span>
                  </div>
                  <Box my={2}>
                    <Typography variant="overline" className={classes.label}>
                      {I18n.t('devices:microphone')}
                    </Typography>
                    <Select
                      className={classes.input}
                      value={activeMicrophone}
                      onChange={changeMicrophone}
                      color="secondary"
                      SelectDisplayProps={{
                        'aria-label': I18n.t('devices:microphone'),
                      }}
                    >
                      {microphoneList.map(({ deviceId, label }) => {
                        return (
                          <MenuItem key={deviceId} value={deviceId}>
                            {label}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {activeMicrophone !== 'none' && (
                      <>
                        <Box my={2}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={audioPassthrough}
                                onChange={handleAudioPassthroughChange}
                                inputProps={{
                                  'aria-label': I18n.t(
                                    'dialog:audio_passthrough:title'
                                  ),
                                }}
                              />
                            }
                            label={
                              <>
                                <Typography variant="body2" component="span">
                                  {I18n.t('dialog:audio_passthrough:title')}
                                </Typography>
                                <Tooltip
                                  arrow
                                  title={I18n.t(
                                    'dialog:audio_passthrough:description'
                                  )}
                                  placement="top"
                                >
                                  <HelpIcon
                                    style={{
                                      opacity: 0.7,
                                      marginInlineStart: '0.3rem',
                                      fontSize: '1rem',
                                    }}
                                  />
                                </Tooltip>
                              </>
                            }
                          />
                        </Box>
                      </>
                    )}
                  </Box>
                  {options.layout_available && (
                    <Box my={2}>
                      <Typography variant="overline" className={classes.label}>
                        {I18n.t('label:fitcontent:description')}
                      </Typography>
                      <RadioGroup
                        aria-label={I18n.t('aria:label:fitcontent')}
                        name="fitContent"
                        value={fitContent}
                        className={classes.radioGroup}
                        onChange={({ target }) => setFitContent(target.value)}
                      >
                        <FormControlLabel
                          value="zoom"
                          control={<Radio />}
                          label={
                            <>
                              <ImageCover className={classes.fitIcon} />
                              <Typography variant="body2" component="span">
                                {I18n.t('label:fitcontent:zoom')}
                              </Typography>
                            </>
                          }
                        />
                        <FormControlLabel
                          value="contain"
                          control={<Radio />}
                          label={
                            <>
                              <ImageContain className={classes.fitIcon} />
                              <Typography variant="body2" component="span">
                                {I18n.t('label:fitcontent:contain')}
                              </Typography>
                            </>
                          }
                        />
                      </RadioGroup>
                    </Box>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </Box>
      <DialogActions>
        {currentState === 'active' ? (
          <div className={classes.actions}>
            <div>
              {options.layout_available &&
                !eyesonSecondCamHelper.isCurrentPresentationLayout() && (
                  <Button
                    color="secondary"
                    variant="outlined"
                    onClick={() => {
                      eyesonSecondCamHelper.setPresentationLayout();
                      onClose();
                    }}
                  >
                    {I18n.t('action:presentation_mode')}
                  </Button>
                )}
            </div>
            <div className={classes.alignRight}>
              <StopButton
                variant="outlined"
                onClick={startCamera}
                className={classes.pushLeft}
              >
                {I18n.t('label:stop')}
              </StopButton>
              <StyledButton color="secondary" onClick={onClose}>
                {I18n.t('label:cancel')}
              </StyledButton>
              {activeCamera !== 'external-camera' && (
                <StyledButton
                  color="secondary"
                  variant="contained"
                  onClick={applyCamera}
                >
                  {I18n.t('btn:apply')}
                </StyledButton>
              )}
            </div>
          </div>
        ) : (
          <>
            <StyledButton color="secondary" onClick={onClose}>
              {activeCamera === 'external-camera'
                ? I18n.t('label:close')
                : I18n.t('label:cancel')}
            </StyledButton>
            {activeCamera !== 'external-camera' && (
              <StyledButton
                color="secondary"
                variant="contained"
                onClick={startCamera}
                disabled={currentState === 'init' || activeCamera === ''}
              >
                {I18n.t('label:start')}
                {currentState === 'init' && (
                  <CircularProgress color="inherit" size="1.2rem" />
                )}
              </StyledButton>
            )}
          </>
        )}
      </DialogActions>
    </>
  );
};

export default SecondCameraDialog;
